import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = '/api';

function SectionHeader({ num, title }) {
  return (
    <div className="sec-divider">
      <span className="sec-num">{num}</span>
      <div className="sec-line" />
      <span className="sec-title">{title}</span>
      <div className="sec-mark" />
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }) +
    " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function StatusLight({ active }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8,
      background: active ? "#22c55e" : "#e5e7eb",
      border: `1px solid ${active ? "#16a34a" : "#d1d5db"}`,
      marginRight: 6, flexShrink: 0,
    }} />
  );
}

export default function AutomationPage() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, logsRes, chRes] = await Promise.all([
        axios.get(`${API}/automation/status`),
        axios.get(`${API}/automation/logs`),
        axios.get(`${API}/channels`),
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data);
      setChannels(chRes.data);
    } catch (e) {
      toast.error("FAILED TO LOAD AUTOMATION DATA");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunNow = async () => {
    setTriggering(true);
    try {
      await axios.post(`${API}/automation/run`);
      toast.success("SCAN TRIGGERED — CHECK LOGS IN A MOMENT");
      setTimeout(fetchData, 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "TRIGGER FAILED");
    } finally {
      setTriggering(false);
    }
  };

  const activeChannels = channels.filter(ch => ch.auto_summarize && ch.channel_id);
  const needsSetup = channels.filter(ch => ch.auto_summarize && !ch.channel_id);

  return (
    <div className="anim-in r-full" style={{ maxWidth: 1000 }} data-testid="automation-page">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#CC0000", marginBottom: 6 }}>
          SYSTEM / BACKGROUND JOBS
        </div>
        <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2rem", fontWeight: 700, color: "#111", margin: 0, lineHeight: 1 }}>AUTOMATION</h1>
        <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
      </div>

      {/* Status + trigger */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader num="01" title="SCHEDULER STATUS" />
        <div className="r-grid-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {/* Status card */}
          <div style={{ background: "#fff", border: "2px solid #111", padding: "24px", position: "relative" }}>
            <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
            <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <StatusLight active={status?.scheduler_running} />
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", fontWeight: 700, color: "#111", letterSpacing: "0.1em" }}>
                {status?.scheduler_running ? "SCHEDULER ACTIVE" : "SCHEDULER OFFLINE"}
              </span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: "POLLING INTERVAL", value: status ? `${status.polling_interval_minutes} MIN` : "—" },
                { label: "ACTIVE CHANNELS", value: loading ? "—" : `${status?.channels_ready || 0} / ${status?.channels_with_auto || 0}` },
                { label: "LAST SCAN", value: formatTime(status?.last_run_at) },
                { label: "NEXT SCAN", value: formatTime(status?.next_run_at) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#bbb", letterSpacing: "0.14em", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.68rem", color: "#111", fontWeight: 600 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Trigger panel */}
          <div style={{ background: "#111", border: "2px solid #111", padding: "24px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.18em", color: "#888", marginBottom: 8 }}>MANUAL TRIGGER</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", fontWeight: 700, color: "#fff", marginBottom: 8, lineHeight: 1.4 }}>
                RUN CHANNEL SCAN NOW
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#666", lineHeight: 1.6 }}>
                Checks all auto-enabled channels for new videos and triggers summarization immediately.
              </div>
            </div>
            <button
              data-testid="run-automation-btn"
              onClick={handleRunNow}
              disabled={triggering || activeChannels.length === 0}
              className="ind-btn-primary"
              style={{ marginTop: 20, background: "#CC0000", borderColor: "#CC0000", justifyContent: "center" }}
            >
              {triggering ? <><Loader2 size={14} className="animate-spin" /> SCANNING...</> : "SCAN NOW →"}
            </button>
            {activeChannels.length === 0 && (
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.52rem", color: "#CC0000", marginTop: 8, letterSpacing: "0.08em" }}>
                NO READY CHANNELS · ADD CHANNEL IDs FIRST
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active channels */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader num="02" title={`AUTO-CHANNELS · ${activeChannels.length} READY`} />
        <div style={{ border: "2px solid #111", background: "#fff" }}>
          <div style={{ display: "grid", gridTemplateColumns: "44px 1fr 140px 120px", background: "#111" }}>
            {["#", "CHANNEL / ID", "LAST CHECKED", "STATUS"].map(h => (
              <div key={h} style={{ padding: "8px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.12em" }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: "24px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#bbb" }}>LOADING...</div>
          ) : activeChannels.length === 0 ? (
            <div style={{ padding: "32px 24px", textAlign: "center" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#bbb", letterSpacing: "0.12em", marginBottom: 8 }}>
                NO CHANNELS READY FOR AUTOMATION
              </div>
              <Link to="/channels" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#CC0000", textDecoration: "none" }}>
                CONFIGURE CHANNELS →
              </Link>
            </div>
          ) : (
            activeChannels.map((ch, i) => (
              <div
                key={ch.id}
                data-testid={`auto-channel-${ch.id}`}
                className="anim-in"
                style={{ display: "grid", gridTemplateColumns: "44px 1fr 140px 120px", borderBottom: i < activeChannels.length - 1 ? "1px solid #e8e8e4" : "none", animationDelay: `${i * 0.04}s`, transition: "background 0.1s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#F9F9F7"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ padding: "12px 10px 12px 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#CC0000", fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</div>
                <div style={{ padding: "12px", minWidth: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.68rem", fontWeight: 600, color: "#111", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ch.name.toUpperCase()}
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888" }}>{ch.channel_id}</div>
                </div>
                <div style={{ padding: "12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#888", display: "flex", alignItems: "center" }}>
                  {ch.last_checked_at ? formatTime(ch.last_checked_at).split(" ")[0] : "NEVER"}
                </div>
                <div style={{ padding: "12px", display: "flex", alignItems: "center", gap: 6 }}>
                  <StatusLight active={true} />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#111" }}>ACTIVE</span>
                  {ch.auto_save_notion && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", background: "#555", color: "#fff", padding: "1px 5px" }}>+NOTION</span>}
                </div>
              </div>
            ))
          )}

          {/* Needs setup warning */}
          {needsSetup.length > 0 && (
            <div style={{ borderTop: "1px solid #e8e8e4", padding: "10px 16px", background: "#fff9f9", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 4, height: 4, background: "#CC0000", flexShrink: 0 }} />
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#CC0000", letterSpacing: "0.08em" }}>
                {needsSetup.length} CHANNEL{needsSetup.length > 1 ? "S" : ""} MISSING CHANNEL ID: {needsSetup.map(c => c.name.toUpperCase()).join(", ")} →{" "}
                <Link to="/channels" style={{ color: "#CC0000", textDecoration: "underline" }}>FIX IN CHANNELS</Link>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Activity log */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div className="sec-divider" style={{ flex: 1, marginBottom: 0 }}>
            <span className="sec-num">03</span>
            <div className="sec-line" />
            <span className="sec-title">ACTIVITY LOG · LAST {logs.length} EVENTS</span>
            <div className="sec-mark" />
          </div>
          <button
            onClick={fetchData}
            style={{ marginLeft: 12, background: "transparent", border: "1px solid #ccc", cursor: "pointer", padding: "4px 10px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#888", transition: "all 0.1s", flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#111"; e.currentTarget.style.color = "#111"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#ccc"; e.currentTarget.style.color = "#888"; }}
            data-testid="refresh-logs-btn"
          >
            ↻ REFRESH
          </button>
        </div>

        <div style={{ border: "2px solid #111", background: "#fff" }} className="table-scroll-wrap">
          <div style={{ minWidth: 600 }}>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 180px 90px", background: "#111" }}>
              {["TIMESTAMP", "VIDEO / CHANNEL", "STATUS MSG", "RESULT"].map(h => (
                <div key={h} style={{ padding: "8px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.12em" }}>{h}</div>
              ))}
            </div>

            {logs.length === 0 ? (
              <div style={{ padding: "40px 24px", textAlign: "center" }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#bbb", letterSpacing: "0.12em" }}>
                  NO ACTIVITY YET · RUN A SCAN OR WAIT FOR SCHEDULER
                </div>
              </div>
            ) : (
              logs.map((log, i) => (
                <div
                  key={log.id}
                  data-testid={`log-row-${log.id}`}
                  className="anim-in"
                  style={{
                    display: "grid", gridTemplateColumns: "140px 1fr 180px 90px",
                    borderBottom: i < logs.length - 1 ? "1px solid #e8e8e4" : "none",
                    animationDelay: `${i * 0.02}s`,
                    background: log.status === "error" ? "rgba(204,0,0,0.02)" : "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#F9F9F7"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = log.status === "error" ? "rgba(204,0,0,0.02)" : "transparent"; }}
                >
                  <div style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888" }}>
                    {formatTime(log.created_at)}
                  </div>
                  <div style={{ padding: "10px 12px", minWidth: 0 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", fontWeight: 600, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
                      {log.video_title.toUpperCase()}
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888" }}>
                      {log.channel_name.toUpperCase()}
                    </div>
                  </div>
                  <div style={{ padding: "10px 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: log.status === "error" ? "#CC0000" : "#555", letterSpacing: "0.06em", display: "flex", alignItems: "center" }}>
                    {log.message || "—"}
                  </div>
                  <div style={{ padding: "10px 12px", display: "flex", alignItems: "center" }}>
                    {log.status === "success" ? (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", background: "#111", color: "#fff", padding: "2px 7px", letterSpacing: "0.1em" }}>OK</span>
                    ) : log.status === "error" ? (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", background: "#CC0000", color: "#fff", padding: "2px 7px", letterSpacing: "0.1em" }}>ERR</span>
                    ) : (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#bbb", letterSpacing: "0.1em" }}>SKIP</span>
                    )}
                    {log.notion_saved && (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", background: "#555", color: "#fff", padding: "2px 5px", marginLeft: 4, letterSpacing: "0.08em" }}>N</span>
                    )}
                    {log.summary_id && (
                      <Link to={`/summary/${log.summary_id}`} style={{ marginLeft: 4 }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.5rem", color: "#CC0000", letterSpacing: "0.08em" }}>→</span>
                      </Link>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* How it works */}
      <div style={{ marginTop: 24, border: "1px solid #e8e8e4", background: "#fff", padding: "16px 20px" }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.16em", color: "#888", marginBottom: 10 }}>HOW AUTOMATION WORKS</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }} className="r-grid-1">
          {[
            { step: "01", text: "Scheduler runs every N minutes per your settings" },
            { step: "02", text: "Checks RSS feed of each auto-enabled channel" },
            { step: "03", text: "Detects new video → fetches transcript → Gemini AI summarizes" },
            { step: "04", text: "Saves summary + optionally pushes to Notion" },
          ].map(({ step, text }) => (
            <div key={step} style={{ display: "flex", gap: 8 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#CC0000", fontWeight: 700, flexShrink: 0 }}>{step}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#555", lineHeight: 1.5, letterSpacing: "0.05em" }}>{text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
